import posthog from 'posthog-js';
import * as Sentry from '@sentry/react';

// Privacy contract: PostHog receives real paths and search queries (product
// decision 2026-08-18). Sentry never does — every URL, transaction name, and
// crumb it sees funnels through sanitizePath first. Neither tool may receive
// email, name, or PWZ.
const STATIC_ROUTES = new Set([
  '/',
  '/login',
  '/signup',
  '/invite',
  '/onboarding',
  '/terms',
  '/privacy',
  '/documents',
  '/feedback',
  '/admin/drugs',
]);

export function sanitizePath(pathname: string): string {
  if (STATIC_ROUTES.has(pathname)) return pathname;
  if (pathname.startsWith('/admin/')) return '/admin/*';
  return '/:query';
}

// Query string and hash are dropped as well: /invite?token=… must not leak.
function sanitizeUrlParts(
  raw: string,
): { url: string; path: string } | undefined {
  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin) return undefined;
    const path = sanitizePath(url.pathname);
    return { url: `${url.origin}${path}`, path };
  } catch {
    return undefined;
  }
}

function sanitizeUrl(raw: string): string {
  return sanitizeUrlParts(raw)?.url ?? raw;
}

let posthogInitialized = false;
let posthogReady = false;
let sentryReady = false;

export function isSentryEnabled(): boolean {
  return sentryReady;
}

/** Starts or stops PostHog. It may run only with the user's consent
 * (`users/{uid}.analytics_opt_in`): its cookie and localStorage are not
 * strictly necessary (Prawo komunikacji elektronicznej art. 399). */
export function setAnalyticsConsent(granted: boolean): void {
  const posthogKey = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN;
  if (!posthogKey) return;

  if (!granted) {
    if (posthogReady) {
      posthog.reset();
      posthog.opt_out_capturing();
      posthogReady = false;
    }
    return;
  }

  if (posthogReady) return;
  if (!posthogInitialized) {
    initPosthog(posthogKey);
    posthogInitialized = true;
  }
  // The opt-out flag outlives the page, so a consent given after an earlier
  // withdrawal must clear it.
  if (posthog.has_opted_out_capturing()) {
    posthog.opt_in_capturing({ captureEventName: false });
  }
  posthogReady = true;
}

function initPosthog(posthogKey: string): void {
  posthog.init(posthogKey, {
    api_host: import.meta.env.VITE_POSTHOG_HOST ?? 'https://eu.i.posthog.com',
    // Clears the PostHog cookie and localStorage when consent is withdrawn.
    opt_out_persistence_by_default: true,
    // Manual pageviews: raw paths go to PostHog deliberately (product
    // decision). Sentry stays sanitized.
    capture_pageview: false,
    respect_dnt: true,
    // Autocapture keeps element shape (tag, classes) but never its text.
    // Search terms flow through pageviews and explicit events only.
    mask_all_text: true,
    mask_all_element_attributes: true,
    session_recording: {
      maskAllInputs: true,
      // Mask ALL visible text in replays, not just inputs: report bodies,
      // recents, and search labels are drug names. Safest default for a
      // medical pilot; relax deliberately, not by accident.
      maskTextSelector: '*',
    },
    // capture() merges the full location.href into every event's
    // $current_url — drop query string and hash so the /invite?token=…
    // credential never reaches PostHog. The path itself stays raw.
    before_send: (cr) => {
      const props = cr?.properties;
      if (props && typeof props.$current_url === 'string') {
        try {
          const url = new URL(props.$current_url, window.location.origin);
          props.$current_url = `${url.origin}${url.pathname}`;
        } catch {
          // Not a URL — leave the property untouched.
        }
      }
      return cr;
    },
  });
}

/** Starts Sentry. PostHog waits for consent: see {@link setAnalyticsConsent}. */
export function initTelemetry(): void {
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  if (sentryDsn) {
    Sentry.init({
      dsn: sentryDsn,
      environment: import.meta.env.MODE,
      sendDefaultPii: false,
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: 0.1,
      beforeSend(event) {
        if (event.request?.url) {
          event.request.url = sanitizeUrl(event.request.url);
        }
        // Error events also carry the active transaction name, which the
        // tracing integration derived from the raw /:query path.
        // beforeSendTransaction does not run for error events.
        if (event.transaction) {
          event.transaction = sanitizePath(event.transaction);
        }
        return event;
      },
      beforeSendTransaction(event) {
        // The tracing integration names transactions after
        // window.location.pathname, which is the raw /:query path.
        if (event.transaction) {
          event.transaction = sanitizePath(event.transaction);
        }
        if (event.request?.url) {
          event.request.url = sanitizeUrl(event.request.url);
        }
        return event;
      },
      beforeBreadcrumb(breadcrumb) {
        const data = breadcrumb.data;
        if (data) {
          for (const key of ['url', 'to', 'from'] as const) {
            if (typeof data[key] === 'string') {
              data[key] = sanitizeUrl(data[key]);
            }
          }
        }
        // ui.* crumb messages are DOM selector strings (htmlTreeAsString) and
        // include attribute values — aria-labels carry drug labels.
        if (breadcrumb.category?.startsWith('ui.')) {
          delete breadcrumb.message;
        }
        return breadcrumb;
      },
    });
    sentryReady = true;
  }
}

// Only the Firebase uid and the profile specialty may leave the browser —
// never email, name, or PWZ.
export function identifyUser(uid: string, specialty?: string): void {
  if (posthogReady) {
    posthog.identify(uid, specialty ? { specialty } : undefined);
  }
  if (sentryReady) {
    Sentry.setUser(specialty ? { id: uid, specialty } : { id: uid });
  }
}

export function resetUser(): void {
  if (posthogReady) posthog.reset();
  if (sentryReady) Sentry.setUser(null);
}

export function trackPageview(path: string): void {
  if (!posthogReady) return;
  posthog.capture('$pageview', {
    $current_url: `${window.location.origin}${path}`,
    $pathname: path,
  });
}

type EventProps = Record<string, string | number | boolean>;

function track(event: string, props?: EventProps): void {
  if (!posthogReady) return;
  posthog.capture(event, props);
}

export function trackSignupStarted(): void {
  track('signup_started');
}

export function trackSignupCompleted(): void {
  track('signup_completed');
}

export function trackInviteRedeemed(): void {
  track('invite_redeemed');
}

export function trackOnboardingCompleted(): void {
  track('onboarding_completed');
}

export function trackTermsAccepted(): void {
  track('terms_accepted');
}

export function trackSearchSubmitted(query: string): void {
  track('search_submitted', { query });
}

export function trackSearchCompleted(props: {
  query: string;
  cache_hit: boolean;
}): void {
  track('search_completed', props);
}

export function trackSearchFailed(props: { query: string }): void {
  track('search_failed', props);
}

export function trackReportViewed(query: string): void {
  track('report_viewed', { query });
}

// The trust-model cross-check for survey Q6/Q7: an outbound click on a cited
// source. `surface` names the link host (citation_list, literature_panel,
// source_chip, decision_panel); the drug stays implicit in $current_url.
export function trackSourceOpened(props: {
  source_type: string;
  surface: string;
}): void {
  track('source_opened', props);
}

export function trackFeedbackSubmitted(): void {
  track('feedback_submitted');
}
